import { TypeaheadSelect } from 'ui/selects';
import { useDebouncedEffect } from 'utils/hooks/useDebouncedEffect';
import { isDefined } from 'utils/tsHelpers';

import { ConditionInputButton } from '../buttons';
import { useTypeAheadState } from './hooks/useTypeAheadState';
import { useSearchFieldContext } from '../context/SearchFieldContext';
import { useTranslation } from '../../../hooks/useTranslation';
import {
  useConditionValueController,
  useConditionFieldNameWatch,
  useConditionValueErrors,
} from '../../../hooks/form';
import { useFieldsContext } from '../../../contexts';
import { StringValue } from '../../../domain';

const getDisplayValue = (value: unknown, placeholder: string) => {
  return `${value}` || placeholder;
};

export const DATA_TEST = {
  input: 'condition-string-input',
};

export const StringInput = () => {
  const { t } = useTranslation();
  const fieldName = useConditionFieldNameWatch();
  const error = useConditionValueErrors();
  const {
    field: { onChange, value: defaultValue },
  } = useConditionValueController();
  const { dataType } = useSearchFieldContext();
  const { isLoading } = useFieldsContext();
  const {
    value,
    isEditing,
    options,
    defaultValues,
    isLoading: isTypeAheadsLoading,
    turnOnEditing,
    turnOffEditing,
    setValue,
  } = useTypeAheadState(
    fieldName,
    defaultValue instanceof StringValue ? defaultValue.value : null,
    dataType,
  );

  const placeholder = t('selector.inputPlaceholder');
  const isStringInputLoading = isLoading || isTypeAheadsLoading;

  useDebouncedEffect(
    () => onChange({ target: { value: value && new StringValue(value) } }),
    [value],
    {
      debounceTimeout: 500,
    },
  );

  if (!isEditing) {
    return (
      <ConditionInputButton
        data-test={DATA_TEST.input}
        onClick={turnOnEditing}
        isError={isDefined(error)}
      >
        {getDisplayValue(value, placeholder)}
      </ConditionInputButton>
    );
  }

  return (
    <TypeaheadSelect
      autoFocus
      defaultValues={defaultValues}
      isLoading={isStringInputLoading}
      options={options}
      onSelect={([option]) => {
        setValue(option);
        turnOffEditing();
      }}
      placeholder={placeholder}
      isError={isDefined(error)}
    />
  );
};
